import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import PermissionsService from "@/core/services/PermissionsService";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: "/",
      redirect: "/luminaries",
      component: () => import("@/layouts/main-layout/MainLayout.vue"),
      children: [
        {
          path: "/luminaries",
          name: "luminaries",
          component: () => import("@/views/projects/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "luminaries",
            breadcrumbs: ["rojects"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/reports",
          name: "reports",
          component: () => import("@/views/reports/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Reports",
            breadcrumbs: ["Reports"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/subscription",
          name: "subscription",
          component: () => import("@/views/subscription/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Subscription",
            breadcrumbs: ["Subscription"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/settings",
          name: "settings",
          component: () => import("@/views/settings/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Settings",
            breadcrumbs: ["Settings"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/administration",
          name: "administration",
          component: () => import("@/views/administration/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Administration",
            breadcrumbs: ["Administration"],
            permissions: {
              administrator: true,
              user: false,
            },
          },
        },
        {
          path: "/guides/add",
          name: "add guide",
          component: () =>
            import("@/views/administration/components/guides/add/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Add Guide",
            breadcrumbs: ["Add Guide"],
            permissions: {
              administrator: true,
              user: false,
            },
          },
        },
        {
          path: "/guides/edit/:id",
          name: "edit guide",
          component: () =>
            import("@/views/administration/components/guides/edit/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Edit Guide",
            breadcrumbs: ["Edit Guide"],
            permissions: {
              administrator: true,
              user: false,
            },
          },
        },
        {
          path: "/guides/:id",
          name: "edit detail",
          component: () =>
            import("@/views/administration/components/guides/detail/index.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Edit Detail",
            breadcrumbs: ["Edit Detail"],
            permissions: {
              administrator: true,
              user: false,
            },
          },
        },
      ],
    },
    {
      path: "/",
      component: () => import("@/layouts/AuthLayout.vue"),
      children: [
        {
          path: "/sign-in",
          name: "sign-in",
          component: () => import("@/views/authentication/SignIn.vue"),
          meta: {
            requiresAuth: false,
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/sign-up",
          name: "sign-up",
          component: () => import("@/views/authentication/SignUp.vue"),
          meta: {
            requiresAuth: false,
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/verify-code",
          name: "verifyCode",
          component: () => import("@/views/authentication/VerifyCode.vue"),
          meta: {
            requiresAuth: false,
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/forgot-password",
          name: "forgot-password",
          component: () => import("@/views/authentication/ForgotPassword.vue"),
          meta: {
            requiresAuth: false,
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/new-password",
          name: "new-password",
          component: () => import("@/views/authentication/NewPassword.vue"),
          meta: {
            requiresAuth: false,
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
      ],
    },
    {
      path: "/",
      component: () => import("@/layouts/SystemLayout.vue"),
      children: [
        {
          path: "/subscription/payment",
          name: "subscription-payment",
          component: () =>
            import("@/views/subscription/components/Payment.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Subscription Payment",
            breadcrumbs: ["Subscription Payment"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/subscription/change-payment-method",
          name: "subscription-change-payment-method",
          component: () =>
            import("@/views/subscription/components/ChangePaymentMethod.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Subscription Change Payment Method",
            breadcrumbs: ["Subscription Change Payment Method"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/subscription/upgrade",
          name: "upgrade-subscription",
          component: () =>
            import("@/views/subscription/components/UpgradeSubscription.vue"),
          meta: {
            requiresAuth: true,
            pageTitle: "Upgrade Subscription",
            breadcrumbs: ["Upgrade Subscription"],
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/contact",
          name: "Contact",
          component: () => import("@/views/contact/index.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/aboutUs",
          name: "About Us",
          component: () => import("@/views/aboutUs/index.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/privacy-policy",
          name: "Privacy Policy",
          component: () => import("@/views/contracts/privacy-policy.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/pdpl-policy",
          name: "PDPL Policy",
          component: () => import("@/views/contracts/kvkk-policy.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/distance-sales-agreement",
          name: "Distance Sales Agreement",
          component: () =>
            import("@/views/contracts/distance-sales-agreement.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
        {
          path: "/refund-policy",
          name: "Refund Policy",
          component: () => import("@/views/contracts/refund-policy.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
      ],
    },
    {
      path: "/",
      component: () => import("@/layouts/SystemLayout.vue"),
      children: [
        {
          // the 404 route, when none of the above matches
          path: "/404",
          name: "404",
          component: () => import("@/views/authentication/Error404.vue"),
          meta: {
            requiresAuth: !!JwtService.getToken(),
            permissions: {
              administrator: true,
              user: true,
            },
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/404",
      meta: {
        requiresAuth: !!JwtService.getToken(),
        permissions: {
          administrator: true,
          user: true,
        },
      },
    },
  ],
});

router.beforeEach((to, from, next) => {
  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);
  const authRequired = to.matched.some((record) => record.meta.requiresAuth);
  const authPermissions = to.meta.permissions as {
    administrator: boolean;
    user: boolean;
  };
  const isToken = JwtService.getToken();
  const permissions = PermissionsService.getPermissions();
  let isPermission = true;

  Object.keys(authPermissions).forEach((permission) => {
    if (permissions.includes(permission)) {
      if (authPermissions[permission]) {
        isPermission = true;
        return;
      } else {
        isPermission = false;
      }
    }
  });

  if (authRequired && !isToken) {
    return next("/sign-in");
  } else if (!authRequired && isToken) {
    return next("/luminaries");
  }

  if (!isPermission) {
    return next("/luminaries");
  }

  next();
  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);
});

export default router;
